/**
 * Voice Service Types
 *
 * These types define the interface for voice calling functionality
 * without being tied to any specific implementation (Twilio, etc.)
 */

export type VoiceConnectionStatus = 'idle' | 'connecting' | 'ringing' | 'connected' | 'reconnecting' | 'disconnected' | 'failed'

export type DeviceStatus = 'registering' | 'registered' | 'unregistered' | 'error'

export type NetworkWarningName =
  | 'high-rtt'
  | 'low-mos'
  | 'high-jitter'
  | 'high-packet-loss'
  | 'high-packets-lost-fraction'
  | 'low-bytes-received'
  | 'low-bytes-sent'
  | 'ice-connectivity-lost'

export type NetworkQuality = 'good' | 'degraded' | 'poor'

export const NETWORK_WARNING_INFO: Record<NetworkWarningName, { label: string; description: string; severity: 'medium' | 'high' | 'critical' }> = {
  'high-rtt': {
    label: 'High latency',
    description: 'Network latency is high. You may notice audio delays.',
    severity: 'medium',
  },
  'low-mos': {
    label: 'Poor call quality',
    description: 'Overall call quality score is low.',
    severity: 'medium',
  },
  'high-jitter': {
    label: 'Unstable connection',
    description: 'Network instability detected. Audio may sound choppy.',
    severity: 'medium',
  },
  'high-packet-loss': {
    label: 'Packet loss',
    description: 'Packets are being lost. Audio quality may be affected.',
    severity: 'high',
  },
  'high-packets-lost-fraction': {
    label: 'High packet loss',
    description: 'Significant packet loss detected. Call quality is degraded.',
    severity: 'high',
  },
  'low-bytes-received': {
    label: 'No audio received',
    description: 'No audio data is being received. You may not be able to hear the other party.',
    severity: 'critical',
  },
  'low-bytes-sent': {
    label: 'No audio sent',
    description: 'No audio data is being sent. The other party may not be able to hear you.',
    severity: 'critical',
  },
  'ice-connectivity-lost': {
    label: 'Connection lost',
    description: 'The call connection was lost and is renegotiating.',
    severity: 'critical',
  },
}

export interface VoiceConnection {
  connectionId: string
  phoneNumber: string
  status: VoiceConnectionStatus
  startTime?: Date
  endTime?: Date
  duration?: number // in seconds
}

export type CallerIdMode = 'verified' | 'cheapest' | 'anonymous'

export interface VoiceCallOptions {
  phoneNumber: string
  callerIdMode?: CallerIdMode
  callerIdNumber?: string
}

export interface VoiceServiceError {
  code: string
  message: string
  originalError?: unknown
}

export interface VoiceDevice {
  deviceId: string
  isReady: boolean
  isRegistered: boolean
}

export interface AudioDevice {
  deviceId: string
  label: string
}

export interface AudioDevices {
  inputs: AudioDevice[]
  outputs: AudioDevice[]
  selectedInput: string | null
  selectedOutput: string | null
}

export interface VoiceServiceEvents {
  onDeviceReady: (device: VoiceDevice) => void
  onDeviceRegistered: (device: VoiceDevice) => void
  onDeviceUnregistered: () => void
  onDeviceError: (error: VoiceServiceError) => void
  onConnectionStatusChange: (connection: VoiceConnection) => void
  onCallStarted: (connection: VoiceConnection) => void
  onCallEnded: (connection: VoiceConnection) => void
  onCallFailed: (connection: VoiceConnection, error: VoiceServiceError) => void
  onReconnecting: (error: VoiceServiceError) => void
  onReconnected: () => void
  onWarning: (warningName: string, warningData?: unknown) => void
  onWarningCleared: (warningName: string) => void
  onTokenWillExpire: () => void
  onTokenExpired: () => void
  onAudioDevicesChanged: () => void
}

export interface VoiceService {
  /**
   * Initialize the voice service with access token
   */
  initialize(accessToken: string, events?: Partial<VoiceServiceEvents>): Promise<VoiceDevice>

  /**
   * Make a phone call
   */
  makeCall(options: VoiceCallOptions): Promise<VoiceConnection>

  /**
   * End the current call
   */
  endCall(): Promise<void>

  /**
   * Send DTMF tones (dial pad digits)
   */
  sendDTMF(digit: string): Promise<void>

  /**
   * Get current connection status
   */
  getConnectionStatus(): VoiceConnectionStatus

  /**
   * Get current active connection
   */
  getActiveConnection(): VoiceConnection | null

  /**
   * Cleanup and destroy the service
   */
  destroy(): void

  /**
   * Check if service is ready to make calls
   */
  isReady(): boolean

  /**
   * Get available audio devices
   */
  getAudioDevices(): AudioDevices

  /**
   * Set the audio input device (microphone)
   */
  setInputDevice(deviceId: string): Promise<void>

  /**
   * Set the audio output device (speaker)
   */
  setOutputDevice(deviceId: string): Promise<void>

  /**
   * Mute/unmute the microphone
   */
  setMuted(muted: boolean): void

  /**
   * Check if currently muted
   */
  isMuted(): boolean

  /**
   * Update the access token (for token refresh)
   */
  updateToken(accessToken: string): void
}
